import React, { useState, useEffect } from "react";
import { router } from "@inertiajs/react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { RadioGroup, RadioGroupItem } from "./ui/radio-group";
import { X } from "lucide-react";

interface Tag {
  id: number;
  name: string;
  color: string;
}

interface LineItem {
  id?: number;
  description?: string;
  tag_ids?: number[];
  tags?: Tag[];
}

interface LineDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  offerId: number;
  item?: LineItem | null;
  availableTags: Tag[];
  // Shared by subject lines and from lines -- defaults keep existing callers
  // working; from lines pass their own resource/param/label.
  resourceType?: string;
  paramKey?: string;
  entityLabel?: string;
}

export default function LineDialog({
  open,
  onOpenChange,
  offerId,
  item = null,
  availableTags = [],
  resourceType = "subject_lines",
  paramKey = "subject_line",
  entityLabel = "Subject Line",
}: LineDialogProps) {
  const [description, setDescription] = useState("");
  const [selectedTagId, setSelectedTagId] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      if (item) {
        setDescription(item.description || "");
        setSelectedTagId(item.tag_ids && item.tag_ids.length > 0 ? item.tag_ids[0] : null);
      } else {
        setDescription("");
        setSelectedTagId(null);
      }
    }
  }, [item, open]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    const data = {
      [paramKey]: { description },
      tag_ids: selectedTagId ? [selectedTagId] : [],
    };

    if (item?.id) {
      router.patch(`/offers/${offerId}/${resourceType}/${item.id}`, data, {
        onFinish: () => {
          setLoading(false);
          onOpenChange(false);
        },
      });
    } else {
      router.post(`/offers/${offerId}/${resourceType}`, data, {
        onFinish: () => {
          setLoading(false);
          onOpenChange(false);
        },
      });
    }
  };


  const getColorClass = (color: string) => {
    switch (color) {
      case "green":
        return "bg-green-100 text-green-800 border-green-200";
      case "yellow":
        return "bg-yellow-100 text-yellow-800 border-yellow-200";
      case "red":
        return "bg-red-100 text-red-800 border-red-200";
      case "blue":
        return "bg-blue-100 text-blue-800 border-blue-200";
      case "purple":
        return "bg-purple-100 text-purple-800 border-purple-200";
      case "gray":
        return "bg-gray-100 text-gray-800 border-gray-200";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[525px]">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>
              {item?.id ? `Edit ${entityLabel}` : `Add ${entityLabel}`}
            </DialogTitle>
            <DialogDescription>
              {item?.id
                ? `Update the ${entityLabel.toLowerCase()} description and tags.`
                : `Add a new ${entityLabel.toLowerCase()} for this offer.`}
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="description">Description *</Label>
              <textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder={`Enter ${entityLabel.toLowerCase()} description...`}
                rows={3}
                className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                required
                disabled={loading}
              />
            </div>

            {availableTags.length > 0 && (
              <div className="space-y-2">
                <Label>Tag (Select one)</Label>
                <RadioGroup
                  value={selectedTagId?.toString() || ""}
                  onValueChange={(value) => setSelectedTagId(value ? parseInt(value) : null)}
                  disabled={loading}
                >
                  <div className="border rounded-lg p-3 space-y-2 max-h-48 overflow-y-auto">
                    {availableTags.map((tag) => (
                      <div
                        key={tag.id}
                        className="flex items-center space-x-2 hover:bg-gray-50 p-2 rounded"
                      >
                        <RadioGroupItem
                          value={tag.id.toString()}
                          id={`tag-${tag.id}`}
                        />
                        <label
                          htmlFor={`tag-${tag.id}`}
                          className="flex-1 cursor-pointer"
                        >
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getColorClass(
                              tag.color
                            )}`}
                          >
                            {tag.name}
                          </span>
                        </label>
                      </div>
                    ))}
                  </div>
                </RadioGroup>
                {selectedTagId && (
                  <div className="flex items-center gap-2 mt-2">
                    <span className="text-sm text-gray-600">Selected:</span>
                    {(() => {
                      const tag = availableTags.find((t) => t.id === selectedTagId);
                      if (!tag) return null;
                      return (
                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${getColorClass(
                            tag.color
                          )}`}
                        >
                          {tag.name}
                          <button
                            type="button"
                            onClick={() => setSelectedTagId(null)}
                            className="ml-1 hover:opacity-75"
                            disabled={loading}
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </span>
                      );
                    })()}
                  </div>
                )}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="bg-gradient-to-b from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 cursor-pointer"
            >
              {loading ? "Saving..." : item?.id ? "Update" : "Create"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}